// src/context/AuthContext.js
import React, { createContext, useState, useEffect, useContext, useRef } from 'react';
import { auth, db } from '../firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc, updateDoc, setDoc, deleteDoc, collection, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { registerMovement } from '../utils/registerMovement';
import { clearOfflineCredentials } from '../utils/authOffline';
import { registerPushNotifications } from '../services/pushNotifications';
import ModalSeleccionInicialPropiedad from '../components/ModalSeleccionInicialPropiedad';
import Swal from 'sweetalert2';

const AuthContext = createContext();

export function useAuth() {
    return useContext(AuthContext);
}

const getOrCreateDeviceId = () => {
    let deviceId = localStorage.getItem('app_device_id');
    if (!deviceId) {
        deviceId = 'dev_' + Math.random().toString(36).substring(2, 11) + '_' + Date.now();
        localStorage.setItem('app_device_id', deviceId);
    }
    return deviceId;
};

export function AuthProvider({ children }) {
    const [currentUser, setCurrentUser] = useState(null);
    const [userData, setUserData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isOfflineAuth, setIsOfflineAuth] = useState(false);
    const [selectedFraccionamientoId, setSelectedFraccionamientoId] = useState(() => localStorage.getItem('active_fraccionamiento_id') || null);
    const [misCasas, setMisCasas] = useState([]);
    const [selectedCasaId, setSelectedCasaId] = useState(() => localStorage.getItem('active_casa_id') || null);
    const [misPropiedadesGlobales, setMisPropiedadesGlobales] = useState([]);
    const [mostrarModalSeleccionInicial, setMostrarModalSeleccionInicial] = useState(false);
    const loginMovementRef = useRef({});
    const unsubSesionesRef = useRef(null);
    const unsubMisCasasRef = useRef(null);
    const unsubGlobalPropiedadesRef = useRef(null);

    const cambiarCasaActiva = (casaId) => {
        if (!casaId) return;
        setSelectedCasaId(casaId);
        localStorage.setItem('active_casa_id', casaId);
        sessionStorage.setItem('session_casa_selected', 'true');
    };

    const cambiarFraccionamiento = (nuevoFraccId) => {
        if (!nuevoFraccId) return;
        setSelectedFraccionamientoId(nuevoFraccId);
        localStorage.setItem('active_fraccionamiento_id', nuevoFraccId);
        sessionStorage.setItem('session_fraccionamiento_selected', 'true');
        if (userData) {
            setUserData(prev => prev ? { ...prev, fraccionamientoId: nuevoFraccId } : prev);
        }
    };

    const logout = async () => {
        // 1. Notificar a otros contextos (ej. ResidentPendingContext) que cancelen listeners inmediatamente
        if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('app:logout_cleanup'));
        }

        // 2. Cancelar listeners activos de AuthContext
        if (unsubSesionesRef.current) {
            try { unsubSesionesRef.current(); unsubSesionesRef.current = null; } catch (e) {}
        }
        if (unsubMisCasasRef.current) {
            try { unsubMisCasasRef.current(); unsubMisCasasRef.current = null; } catch (e) {}
        }
        if (unsubGlobalPropiedadesRef.current) {
            try { unsubGlobalPropiedadesRef.current(); unsubGlobalPropiedadesRef.current = null; } catch (e) {}
        }

        // 3. Limpiar estado de usuario en React
        setUserData(null);
        setCurrentUser(null);
        setSelectedFraccionamientoId(null);
        setSelectedCasaId(null);
        setMisCasas([]);
        setMisPropiedadesGlobales([]);

        // Pausa breve de 50ms para permitir que Firestore remueva targets antes de invalidar auth
        await new Promise((resolve) => setTimeout(resolve, 50));

        // 4. Eliminar registro de sesión activa del dispositivo
        try {
            const deviceId = localStorage.getItem('app_device_id');
            if (auth.currentUser && deviceId) {
                await deleteDoc(doc(db, 'usuarios', auth.currentUser.uid, 'sesiones_activas', deviceId)).catch(() => {});
            }
        } catch (e) {
            console.warn('Error eliminando sesión activa al logout:', e);
        }

        localStorage.removeItem('active_fraccionamiento_id');
        localStorage.removeItem('active_casa_id');
        sessionStorage.removeItem('active_fraccionamiento_id');
        sessionStorage.removeItem('session_fraccionamiento_selected');
        sessionStorage.removeItem('session_casa_selected');
        clearOfflineCredentials();
        setIsOfflineAuth(false);
        return signOut(auth);
    };

    const setOfflineUser = (uid, email) => {
        const mockUser = { uid, email, isOffline: true };
        setCurrentUser(mockUser);
        setIsOfflineAuth(true);
        localStorage.setItem('offline_session_active', JSON.stringify({ uid, email }));
        
        // Populate userData immediately so navigation doesn't instantly bounce back to public routes
        const savedData = localStorage.getItem('offline_userData');
        if (savedData) {
            setUserData(JSON.parse(savedData));
        }
    };

    useEffect(() => {
        console.log("AUTH_CONTEXT: useEffect se está ejecutando.");

        let unsubSesiones = null;

        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            console.log("AUTH_CONTEXT: onAuthStateChanged se disparó.");
            
            let finalUser = user;

            // 1. Si no hay usuario de Firebase pero existe una sesión offline activa
            if (!finalUser) {
                try {
                    const stored = localStorage.getItem('offline_session_active');
                    if (stored) {
                        const data = JSON.parse(stored);
                        console.log("AUTH_CONTEXT: Restaurando sesión offline para UID:", data.uid);
                        finalUser = { uid: data.uid, email: data.email, isOffline: true };
                        setIsOfflineAuth(true);
                    }
                } catch (e) {
                    console.error("AUTH_CONTEXT: Error al leer sesión offline", e);
                }
            }

            setCurrentUser(finalUser);

            if (finalUser) {
                console.log(`AUTH_CONTEXT: Usuario detectado con UID: ${finalUser.uid}`);
                
                // --- REGISTRO Y MONITOREO DE SESIÓN ACTIVA (PLAN B) ---
                if (!finalUser.isOffline) {
                    const deviceId = getOrCreateDeviceId();
                    const userAgent = typeof window !== 'undefined' ? window.navigator.userAgent : '';
                    const sesionRef = doc(db, 'usuarios', finalUser.uid, 'sesiones_activas', deviceId);
                    
                    setDoc(sesionRef, {
                        deviceId,
                        userAgent,
                        ultimoAcceso: serverTimestamp(),
                        estado: 'activa',
                        userId: finalUser.uid,
                        email: finalUser.email || '',
                    }, { merge: true }).catch(err => console.warn('AUTH_CONTEXT: Error registrando sesión activa', err));

                    // Escuchar colección de sesiones activas del usuario
                    let localSessionWasActive = false;
                    const sesionesCollRef = collection(db, 'usuarios', finalUser.uid, 'sesiones_activas');
                    unsubSesiones = onSnapshot(sesionesCollRef, (snap) => {
                        const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
                        const localExists = docs.some(d => d.id === deviceId);

                        if (localExists) {
                            localSessionWasActive = true;
                        }

                        // Si la sesión de este dispositivo estuvo activa y fue revocada/eliminada
                        if (!localExists && localSessionWasActive && !finalUser.isOffline) {
                            localSessionWasActive = false;
                            Swal.fire({
                                title: 'Sesión Finalizada',
                                text: 'Tu sesión en este dispositivo ha sido cerrada.',
                                icon: 'warning',
                                confirmButtonColor: '#0284c7'
                            });
                            logout();
                            return;
                        }

                        // Límite máximo de 3 sesiones simultáneas por cuenta
                        if (docs.length > 3) {
                            // Filtramos las sesiones para excluir el dispositivo actual y evitar auto-expulsión
                            const otrasSesiones = docs.filter(d => d.id !== deviceId);
                            
                            // Ordenamos las otras sesiones de la más antigua a la más reciente
                            otrasSesiones.sort((a, b) => {
                                const tA = a.ultimoAcceso?.toDate ? a.ultimoAcceso.toDate().getTime() : 0;
                                const tB = b.ultimoAcceso?.toDate ? b.ultimoAcceso.toDate().getTime() : 0;
                                return tA - tB;
                            });
                            
                            // Expulsamos la sesión más antigua de los otros dispositivos
                            if (otrasSesiones.length > 0) {
                                const oldest = otrasSesiones[0];
                                deleteDoc(doc(db, 'usuarios', finalUser.uid, 'sesiones_activas', oldest.id))
                                    .catch(err => console.warn('AUTH_CONTEXT: Error al expulsar la sesión más antigua', err));
                            }
                        }
                    });
                    unsubSesionesRef.current = unsubSesiones;
                }
                // -----------------------------------------------------

                // Registrar el dispositivo en FCM para notificaciones push si estamos en móvil
                if (!finalUser.isOffline) {
                    registerPushNotifications(finalUser.uid).catch(err => {
                        console.warn("AUTH_CONTEXT: Error silencioso al intentar registrar Push Notifications:", err);
                    });
                }

                // 2. ESTRATEGIA CACHE-FIRST: Cargar datos locales inmediatamente para evitar logout prematuro
                const savedData = localStorage.getItem('offline_userData');
                let localCacheRestored = false;
                if (savedData) {
                    try {
                        const parsed = JSON.parse(savedData);
                        if (parsed && (parsed.uid === finalUser.uid || !parsed.uid)) {
                            console.log("AUTH_CONTEXT: [Cache-First] Restaurando userData localmente mientras sincroniza...");
                            setUserData(parsed);
                            localCacheRestored = true;
                        }
                    } catch (e) {
                        console.warn("AUTH_CONTEXT: Error al parsear caché de usuario", e);
                    }
                }

                try {
                    const userDocRef = doc(db, 'usuarios', finalUser.uid);
                    let userDoc;
                    try {
                        userDoc = await getDoc(userDocRef);
                    } catch (getDocError) {
                        console.warn("AUTH_CONTEXT: Fallo fetch de Firestore (posible offline o reconexión flaky):", getDocError);
                    }

                    if (userDoc && userDoc.exists()) {
                        const rawData = userDoc.data();

                        // --- BLOQUE CUENTA SUSPENDIDA ---
                        if (rawData.suspendido === true && navigator.onLine) {
                            console.warn("AUTH_CONTEXT: Cuenta suspendida detectada.");
                            await signOut(auth);
                            setUserData(null);
                            setCurrentUser(null);
                            setLoading(false);
                            return;
                        }
                        // -------------------------------

                        const data = { ...rawData, uid: finalUser.uid };

                        if (!data.fraccionamientoId && data.fraccionamientoTd) {
                            data.fraccionamientoId = data.fraccionamientoTd;
                        }

                        // Determinar la selección de fraccionamiento según cantidad de fraccionamientos asignados
                        const currentActive = localStorage.getItem('active_fraccionamiento_id');
                        const isSessionSelected = sessionStorage.getItem('session_fraccionamiento_selected') === 'true';
                        const assignedSet = new Set();
                        if (data.fraccionamientoId) assignedSet.add(data.fraccionamientoId);
                        if (data.fraccionamientoTd) assignedSet.add(data.fraccionamientoTd);
                        if (Array.isArray(data.fraccionamientosAsignados)) {
                            data.fraccionamientosAsignados.forEach((item) => {
                                if (typeof item === 'string') assignedSet.add(item);
                                else if (item && item.id) assignedSet.add(item.id);
                            });
                        }

                        if (assignedSet.size > 1) {
                            if (isSessionSelected && currentActive && assignedSet.has(currentActive)) {
                                data.fraccionamientoId = currentActive;
                                setSelectedFraccionamientoId(currentActive);
                            } else {
                                // Múltiples fraccionamientos: NO auto-seleccionar ninguno hasta que el usuario elija en el modal
                                data.fraccionamientoId = null;
                                setSelectedFraccionamientoId(null);
                                localStorage.removeItem('active_fraccionamiento_id');
                                sessionStorage.removeItem('session_fraccionamiento_selected');
                            }
                        } else if (assignedSet.size === 1) {
                            const singleId = Array.from(assignedSet)[0];
                            data.fraccionamientoId = singleId;
                            setSelectedFraccionamientoId(singleId);
                            localStorage.setItem('active_fraccionamiento_id', singleId);
                            sessionStorage.setItem('session_fraccionamiento_selected', 'true');
                        }

                        console.log("AUTH_CONTEXT: [Fresh] Datos actualizados desde Firestore.");
                        setUserData(data);
                        localStorage.setItem('offline_userData', JSON.stringify(data));

                        // Actualizar documento de sesión activa con metadatos completos
                        if (!finalUser.isOffline) {
                            const deviceId = getOrCreateDeviceId();
                            const sesionRef = doc(db, 'usuarios', finalUser.uid, 'sesiones_activas', deviceId);
                            setDoc(sesionRef, {
                                fraccionamientoId: data.fraccionamientoId || null,
                                nombre: data.nombre || data.displayName || finalUser.displayName || '',
                                email: data.email || finalUser.email || '',
                                rol: data.rol || 'sin-rol',
                                ultimoAcceso: serverTimestamp(),
                            }, { merge: true }).catch(() => {});
                        }

                        // Sincronizar fraccionamientos asignados automáticamente para administradores
                        const esAdmin = ['superadmin', 'platformadmin', 'admin'].includes(data.rol);
                        if (esAdmin && !finalUser.isOffline && navigator.onLine) {
                            (async () => {
                                try {
                                    const functions = getFunctions();
                                    const syncFn = httpsCallable(functions, 'sincronizarMisFraccionamientos');
                                    const res = await syncFn({});
                                    if (res?.data?.success && Array.isArray(res.data.fraccionamientosAsignados)) {
                                        const newAssigned = res.data.fraccionamientosAsignados;
                                        setUserData((prev) => prev ? { ...prev, fraccionamientosAsignados: newAssigned } : prev);
                                    }
                                } catch (syncErr) {
                                    console.warn('AUTH_CONTEXT: Error silencioso sincronizando fraccionamientos:', syncErr);
                                }
                            })();
                        }
                    } else {
                        console.warn(`AUTH_CONTEXT: No se pudo obtener doc fresco para UID: ${finalUser.uid}`);
                        if (localCacheRestored) {
                            console.log("AUTH_CONTEXT: Manteniendo sesión activa con caché local tras fallo de red.");
                        } else if (!finalUser.isOffline && navigator.onLine) {
                            try {
                                const functions = getFunctions();
                                const autoLink = httpsCallable(functions, 'autoLinkMyUser');
                                const res = await autoLink({});
                                if (res?.data?.success) {
                                    const retryDoc = await getDoc(userDocRef);
                                    if (retryDoc.exists()) {
                                        const freshData = { ...retryDoc.data(), uid: finalUser.uid };
                                        setUserData(freshData);
                                        localStorage.setItem('offline_userData', JSON.stringify(freshData));
                                    }
                                }
                            } catch (autoErr) {
                                console.error('AUTH_CONTEXT: autoLink falló:', autoErr);
                                setUserData(null);
                            }
                        }
                    }
                } catch (error) {
                    console.error("AUTH_CONTEXT: Error crítico en manejo de usuario:", error);
                    if (!localCacheRestored) {
                        setUserData(null);
                    }
                    loginMovementRef.current = {};
                }
            } else {
                console.log("AUTH_CONTEXT: No hay usuario logueado.");
                setUserData(null);
                loginMovementRef.current = {};
            }

            console.log("AUTH_CONTEXT: Terminando carga.");
            setLoading(false);
        });

        return () => {
            unsubscribe();
            if (unsubSesionesRef.current) {
                unsubSesionesRef.current();
                unsubSesionesRef.current = null;
            } else if (unsubSesiones) {
                unsubSesiones();
            }
        };
    }, []);

    const activeFraccionamientoId = selectedFraccionamientoId || userData?.fraccionamientoId || userData?.fraccionamientoTd || null;

    useEffect(() => {
        let unsub = null;
        const loadMisCasas = async () => {
            if (!currentUser || !userData || !activeFraccionamientoId) {
                setMisCasas([]);
                return;
            }

            const userEmail = (userData.email || currentUser.email || '').toLowerCase().trim();
            if (!userEmail) {
                setMisCasas([]);
                return;
            }

            try {
                const { collection, query, where, onSnapshot } = await import('firebase/firestore');
                const casasRef = collection(db, 'fraccionamientos', activeFraccionamientoId, 'casas');
                const q1 = query(casasRef, where('email', '==', userEmail));
                const q2 = query(casasRef, where('emailPropietario', '==', userEmail));

                let snap1Docs = [];
                let snap2Docs = [];

                const updateList = () => {
                    const map = new Map();
                    snap1Docs.forEach(d => map.set(d.id, { id: d.id, ...d.data() }));
                    snap2Docs.forEach(d => {
                        if (!map.has(d.id)) {
                            map.set(d.id, { id: d.id, esPropietario: true, ...d.data() });
                        }
                    });
                    const list = Array.from(map.values());
                    setMisCasas(list);

                    if (list.length === 1) {
                        setSelectedCasaId(list[0].id);
                        localStorage.setItem('active_casa_id', list[0].id);
                    } else if (list.length > 1) {
                        const currentStored = localStorage.getItem('active_casa_id');
                        const exists = list.some(c => c.id === currentStored);
                        if (!exists) {
                            setSelectedCasaId(list[0].id);
                            localStorage.setItem('active_casa_id', list[0].id);
                        }
                    }
                };

                const unsub1 = onSnapshot(q1, (snap) => {
                    snap1Docs = snap.docs;
                    updateList();
                }, (err) => {
                    console.warn('AuthContext: Error escuchando casas del residente:', err);
                });

                const unsub2 = onSnapshot(q2, (snap) => {
                    snap2Docs = snap.docs;
                    updateList();
                }, (err) => {
                    console.warn('AuthContext: Error escuchando casas del propietario:', err);
                });

                unsub = () => {
                    unsub1();
                    unsub2();
                };
                unsubMisCasasRef.current = unsub;
            } catch (err) {
                console.warn('AuthContext: Error cargando misCasas:', err);
            }
        };

        loadMisCasas();

        return () => {
            if (unsubMisCasasRef.current) {
                unsubMisCasasRef.current();
                unsubMisCasasRef.current = null;
            } else if (unsub) {
                unsub();
            }
        };
    }, [currentUser, userData?.email, activeFraccionamientoId]);

    // --- DESCUBRIMIENTO GLOBAL DE PROPIEDADES (MULTI-FRACCIONAMIENTO: INQUILINO Y PROPIETARIO) ---
    useEffect(() => {
        let unsub = null;
        const loadGlobalProperties = async () => {
            if (!currentUser || !userData) {
                setMisPropiedadesGlobales([]);
                return;
            }

            const userEmail = (userData.email || currentUser.email || '').toLowerCase().trim();
            if (!userEmail) {
                setMisPropiedadesGlobales([]);
                return;
            }

            try {
                const { collectionGroup, query, where, onSnapshot } = await import('firebase/firestore');
                const qGroup1 = query(collectionGroup(db, 'casas'), where('email', '==', userEmail));
                const qGroup2 = query(collectionGroup(db, 'casas'), where('emailPropietario', '==', userEmail));

                let docs1 = [];
                let docs2 = [];

                const processSnaps = () => {
                    const groupMap = {};
                    const seenCasaIds = new Set();

                    [...docs1, ...docs2].forEach(d => {
                        if (seenCasaIds.has(d.id)) return;
                        seenCasaIds.add(d.id);

                        const houseData = d.data() || {};
                        const houseId = d.id;
                        const fraccId = houseData.fraccionamientoId || d.ref.parent?.parent?.id;
                        if (!fraccId) return;

                        if (!groupMap[fraccId]) {
                            groupMap[fraccId] = {
                                fraccionamientoId: fraccId,
                                casas: []
                            };
                        }
                        groupMap[fraccId].casas.push({ id: houseId, fraccionamientoId: fraccId, ...houseData });
                    });

                    const list = Object.values(groupMap);
                    setMisPropiedadesGlobales(list);

                    // Sincronizar fraccionamientosAsignados en userData
                    const discoveredFraccIds = list.map(g => g.fraccionamientoId);
                    if (discoveredFraccIds.length > 0) {
                        setUserData(prev => {
                            if (!prev) return prev;
                            const esAdmin = ['superadmin', 'platformadmin', 'admin'].includes(prev.rol);
                            let updatedList;
                            if (esAdmin) {
                                const existingAssigned = Array.isArray(prev.fraccionamientosAsignados)
                                    ? prev.fraccionamientosAsignados
                                    : [];
                                updatedList = Array.from(new Set([...existingAssigned, ...discoveredFraccIds]));
                            } else {
                                updatedList = discoveredFraccIds;
                            }
                            return { ...prev, fraccionamientosAsignados: updatedList };
                        });
                    }

                    // Determinar si debemos mostrar el modal inicial de selección tras el login
                    const isSessionSelected = sessionStorage.getItem('session_fraccionamiento_selected') === 'true';
                    if (list.length > 1 || (list.length === 1 && list[0].casas.length > 1)) {
                        if (!isSessionSelected) {
                            setMostrarModalSeleccionInicial(true);
                        }
                    } else if (list.length === 1 && list[0].casas.length <= 1) {
                        // Exactamente 1 casa en 1 fraccionamiento: entrar directamente sin modal
                        setMostrarModalSeleccionInicial(false);
                        const singleFraccId = list[0].fraccionamientoId;
                        if (!activeFraccionamientoId || activeFraccionamientoId !== singleFraccId) {
                            setSelectedFraccionamientoId(singleFraccId);
                            localStorage.setItem('active_fraccionamiento_id', singleFraccId);
                            sessionStorage.setItem('session_fraccionamiento_selected', 'true');
                        }
                    }
                };

                const unsub1 = onSnapshot(qGroup1, (snap) => {
                    docs1 = snap.docs;
                    processSnaps();
                }, async (err) => {
                    console.warn('AuthContext: Error escuchando collectionGroup casas por email:', err);
                });

                let unsub2 = () => {};
                try {
                    unsub2 = onSnapshot(qGroup2, (snap) => {
                        docs2 = snap.docs;
                        processSnaps();
                    }, (err) => {
                        console.warn('AuthContext: Error escuchando collectionGroup casas por emailPropietario:', err);
                    });
                } catch (e2) {
                    console.warn('AuthContext: Fallback sin listener de emailPropietario:', e2);
                }

                unsub = () => {
                    unsub1();
                    unsub2();
                };
                unsubGlobalPropiedadesRef.current = unsub;
            } catch (err) {
                console.warn('AuthContext: Error en loadGlobalProperties:', err);
            }
        };

        loadGlobalProperties();


        return () => {
            if (unsubGlobalPropiedadesRef.current) {
                unsubGlobalPropiedadesRef.current();
                unsubGlobalPropiedadesRef.current = null;
            } else if (unsub) {
                unsub();
            }
        };
    }, [currentUser, userData?.email]);

    const seleccionarPropiedadDesdeModal = (fraccId, casaId) => {
        if (!fraccId) return;
        cambiarFraccionamiento(fraccId);
        if (casaId) {
            cambiarCasaActiva(casaId);
        }
        sessionStorage.setItem('session_fraccionamiento_selected', 'true');
        setMostrarModalSeleccionInicial(false);
    };

    const casaActiva = misCasas.length > 0
        ? (selectedCasaId ? misCasas.find(c => c.id === selectedCasaId) || misCasas[0] : misCasas[0])
        : null;

    const updateLocalUserData = (updates = {}) => {
        setUserData((prev) => {
            if (!prev) return prev;
            const updated = { ...prev, ...updates };
            try {
                localStorage.setItem('offline_userData', JSON.stringify(updated));
            } catch (e) {
                console.warn('AUTH_CONTEXT: Error guardando offline_userData en updateLocalUserData', e);
            }
            return updated;
        });
    };

    const value = {
        currentUser,
        userData,
        updateLocalUserData,
        loading,
        logout,
        setOfflineUser,
        isOfflineAuth,
        fraccionamientoId: activeFraccionamientoId,
        activeFraccionamientoId,
        cambiarFraccionamiento,
        misCasas,
        casaActiva,
        casaActivaId: casaActiva?.id || selectedCasaId || null,
        cambiarCasaActiva,
        misPropiedadesGlobales,
        mostrarModalSeleccionInicial,
        setMostrarModalSeleccionInicial,
        seleccionarPropiedadDesdeModal,
    };

    useEffect(() => {
        if (!currentUser || !userData) {
            return;
        }

        const collectFraccionamientoIds = (data) => {
            const unique = new Set();

            const pushValue = (value) => {
                if (typeof value === 'string') {
                    const trimmed = value.trim();
                    if (trimmed.length > 0) {
                        unique.add(trimmed);
                    }
                }
            };

            pushValue(data.fraccionamientoId);
            pushValue(data.fraccionamientoTd);
            pushValue(data.fraccionamientoSeleccionado);
            pushValue(data.fraccionamientoSeleccionadoId);

            const assigned = Array.isArray(data.fraccionamientosAsignados)
                ? data.fraccionamientosAsignados
                : [];
            assigned.forEach((item) => {
                if (typeof item === 'string') {
                    pushValue(item);
                } else if (item && typeof item === 'object') {
                    pushValue(item.id);
                    pushValue(item.fraccionamientoId);
                    pushValue(item.fraccionamiento);
                }
            });

            const legacyAssigned = Array.isArray(data.fraccionamientos)
                ? data.fraccionamientos
                : [];
            legacyAssigned.forEach((item) => {
                if (typeof item === 'string') {
                    pushValue(item);
                } else if (item && typeof item === 'object') {
                    pushValue(item.id);
                    pushValue(item.fraccionamientoId);
                    pushValue(item.fraccionamiento);
                }
            });

            return Array.from(unique);
        };

        const fraccionamientos = collectFraccionamientoIds(userData);
        if (fraccionamientos.length === 0) {
            return;
        }

        const nombre = [
            userData.nombreCompleto,
            userData.nombre,
            userData.displayName,
            currentUser.displayName,
            currentUser.email,
            currentUser.uid,
        ].find((value) => typeof value === 'string' && value.trim().length > 0) || currentUser.uid;

        const rol = typeof userData.rol === 'string' && userData.rol.trim().length > 0
            ? userData.rol.trim()
            : 'sin-rol';

        const buildDescripcion = () => {
            if (!nombre && !rol) {
                return 'Se registró un nuevo inicio de sesión.';
            }
            if (!rol || rol === 'sin-rol') {
                return `El usuario ${nombre} inició sesión.`;
            }
            return `El usuario ${nombre} inició sesión con el rol ${rol}.`;
        };

        const descripcion = buildDescripcion();

        const userAgent = typeof window !== 'undefined' ? window.navigator.userAgent : null;

        fraccionamientos.forEach((fraccionamientoId) => {
            const cacheKey = `${currentUser.uid}:${fraccionamientoId}`;
            const currentState = loginMovementRef.current[cacheKey];
            if (currentState === 'pending' || currentState === 'done' || currentState === 'failed') {
                return;
            }

            loginMovementRef.current[cacheKey] = 'pending';

            // Registrar movimiento de forma asíncrona sin bloquear la navegación
            (async () => {
                try {
                    // Solo intentar si tenemos fraccionamientoId
                    if (!fraccionamientoId) {
                        console.warn('AUTH_CONTEXT: No se puede registrar movimiento sin fraccionamientoId');
                        loginMovementRef.current[cacheKey] = 'skipped';
                        return;
                    }
                    if (currentUser?.isOffline) {
                        console.log('AUTH_CONTEXT: Omitiendo registro de movimiento por inicio de sesión offline simulado.');
                        loginMovementRef.current[cacheKey] = 'done';
                        return;
                    }

                    await registerMovement({
                        fraccionamientoId,
                        titulo: `Inicio de sesión - ${nombre}`,
                        descripcion,
                        categoria: 'Sesiones',
                        tipoMovimiento: 'inicio_sesion',
                        origen: 'autenticacion',
                        metadata: {
                            email: currentUser.email || null,
                            rol,
                            userAgent,
                            loginUid: currentUser.uid,
                            loginFraccionamientoId: fraccionamientoId,
                        },
                    });
                    loginMovementRef.current[cacheKey] = 'done';
                    console.log('AUTH_CONTEXT: Movimiento de inicio de sesión registrado exitosamente');
                } catch (movementError) {
                    // Solo registrar el error, no bloqueamos la navegación
                    console.warn('AUTH_CONTEXT: no se pudo registrar el inicio de sesión en notificaciones', movementError);
                    loginMovementRef.current[cacheKey] = 'failed';
                }
            })();
        });
    }, [currentUser, userData, loginMovementRef]);

    // --- LÓGICA DE PRESENCIA GLOBAL (CHAT) ---
    useEffect(() => {
        if (!currentUser || !currentUser.uid || currentUser.isOffline) {
            return undefined;
        }

        const userDocRef = doc(db, 'usuarios', currentUser.uid);

        const updatePresence = (state) => {
            updateDoc(userDocRef, {
                chatPresence: {
                    state,
                    updatedAt: serverTimestamp(),
                },
            }).catch((error) => {
                console.error('AUTH_CONTEXT: No se pudo actualizar la presencia en el chat', error);
            });
        };

        // Estado inicial al conectar
        updatePresence('online');

        // Heartbeat cada 60 segundos para mantener el estado "En Línea"
        const heartbeat = setInterval(() => updatePresence('online'), 60_000);

        // Detectar si el usuario cambia de pestaña o minimiza la app
        const handleVisibility = () => {
            if (document.visibilityState === 'hidden') {
                updatePresence('away');
            } else {
                updatePresence('online');
            }
        };

        // Detectar cierre de ventana/pestaña
        const handleBeforeUnload = () => updatePresence('offline');

        document.addEventListener('visibilitychange', handleVisibility);
        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            clearInterval(heartbeat);
            document.removeEventListener('visibilitychange', handleVisibility);
            window.removeEventListener('beforeunload', handleBeforeUnload);
            // Solo marcar offline si estamos seguros de que no es un cambio de ruta dentro de la SPA
            // updatePresence('offline'); // Opcional, el Heartbeat (2 min threshold) es más seguro
        };
    }, [currentUser]);

    useEffect(() => {
        if (typeof window !== 'undefined') {
            window.__authDebug = {
                currentUser,
                userData,
                loading,
            };
        }
        console.log("AUTH_CONTEXT: Estado actual", { currentUser, userData, loading });
    }, [currentUser, userData, loading]);

    console.log("AUTH_CONTEXT: Renderizando Provider con el valor:", value);

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
            {mostrarModalSeleccionInicial && (
                <ModalSeleccionInicialPropiedad
                    propiedadesGlobales={misPropiedadesGlobales}
                    onSelectPropiedad={seleccionarPropiedadDesdeModal}
                    onClose={() => setMostrarModalSeleccionInicial(false)}
                />
            )}
        </AuthContext.Provider>
    );
}